import './ServiceOrderShippedItems.scss';

import { ExclamationCircleOutlined, ExportOutlined } from '@ant-design/icons';
import type { CollapseProps } from 'antd';
import { Alert, Button, Collapse, Empty, Flex, Skeleton, Tooltip } from 'antd';
import { type Dispatch, type SetStateAction, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Package } from '@/model/Logistic/Package';
import { PackageItem } from '@/model/Logistic/PackageItem/PackageItem';
import { SHIPPING_ITEM_REFERENCE } from '@/model/Logistic/Shipment/ShipmentReference';
import { ServiceOrderItem } from '@/model/ServiceOrder/ServiceOrderItem';
import { RESOURCES } from '@/Resources';

import ServiceOrderShippedItemsTable from './ServiceOrderShippedItemsTable';

interface ServiceOrderShippedItemsProps {
  serviceOrderItems: ServiceOrderItem[];
  // Permite updates funcionales para evitar sobreescrituras.
  setServiceOrderItems: Dispatch<SetStateAction<ServiceOrderItem[]>>;
  shippedItems: PackageItem[];
  isLoading: boolean;
  tickets: (number | string)[];
  onInvalidDistributionChange?: (hasInvalid: boolean) => void;
}

const isNoReturnOrSaleReference = (reference?: SHIPPING_ITEM_REFERENCE) =>
  reference === SHIPPING_ITEM_REFERENCE.SIN_DEVOLUCION ||
  reference === SHIPPING_ITEM_REFERENCE.VENTA_REPUESTO;
const ServiceOrderShippedItems = (props: ServiceOrderShippedItemsProps) => {
  const { t } = useTranslation();
  const {
    serviceOrderItems,
    setServiceOrderItems,
    shippedItems,
    isLoading,
    tickets,
    onInvalidDistributionChange,
  } = props;
  // Validaciones de cantidades
  const shippedItemData = useMemo(() => {
    const data = new Map<number, { maxAmount: number; reference?: SHIPPING_ITEM_REFERENCE }>();

    shippedItems.forEach(item => {
      if (typeof item.id !== 'number') {
        return;
      }

      data.set(item.id, {
        maxAmount: Math.max(item.execute_amount ?? 0, 0),
        reference: item.item_shipped_reference,
      });
    });

    return data;
  }, [shippedItems]);
  // Validación de cantidades.
  const invalidDistributionIds = useMemo(
    () =>
      serviceOrderItems
        .map(item => {
          const packageItemId =
            typeof item.package_item === 'number' ? item.package_item : item.package_item?.id;

          if (!packageItemId) {
            return undefined;
          }

          const package_item = shippedItemData.get(packageItemId);

          if (!package_item || isNoReturnOrSaleReference(package_item.reference)) {
            return undefined;
          }

          const total =
            (item.stock_technician_amount ?? 0) +
            (item.discarded_amount ?? 0) +
            (item.to_return_amount ?? 0);

          return total !== package_item.maxAmount ? packageItemId : undefined;
        })
        .filter((id): id is number => typeof id === 'number'),
    [serviceOrderItems, shippedItemData],
  );
  const hasInvalidDistributionFlag = invalidDistributionIds.length > 0;
  const invalidDistributionIdsSet = useMemo(
    () => new Set(invalidDistributionIds),
    [invalidDistributionIds],
  );

  useEffect(() => {
    onInvalidDistributionChange?.(hasInvalidDistributionFlag);
  }, [hasInvalidDistributionFlag, onInvalidDistributionChange]);
  // Armado de los grupos de paquetes
  const packageGroups = useMemo(() => {
    const groups = new Map<string, { packageId?: number; pkg?: Package; items: PackageItem[] }>();

    shippedItems.forEach(item => {
      const packageId =
        item.package && typeof item.package !== 'number' ? item.package.id : item.package;
      const groupKey = packageId ? packageId.toString() : 'no-package';
      const pkg = typeof item.package === 'number' ? undefined : item.package;
      const group = groups.get(groupKey) ?? { packageId, pkg, items: [] };

      group.items.push(item);
      if (!group.pkg && pkg) {
        group.pkg = pkg;
      }

      groups.set(groupKey, group);
    });

    return Array.from(groups.values()).sort((a, b) => (a.packageId ?? 0) - (b.packageId ?? 0));
  }, [shippedItems]);
  // Armado de los paneles de paquetes para cada collapse
  const packagePanels: CollapseProps['items'] = useMemo(
    () =>
      packageGroups.map(group => ({
        key: group.packageId ? group.packageId.toString() : 'no-package',
        label: (
          <Flex
            justify="space-between"
            align="center"
            className="ServiceOrderShippedItemsPackageHeader">
            <div className="ServiceOrderShippedItemsPackageTitle">
              {`${t('Envío')} #${group.packageId ?? 'no-package'}`}

              <Button
                size="small"
                type="text"
                icon={<ExportOutlined />}
                onClick={e => {
                  e.stopPropagation();
                  e.preventDefault();
                  window.open(`/v2/${RESOURCES.SHIPMENTS}/show/${group.packageId}`);
                }}
              />
              {group.items.some(
                item => typeof item.id === 'number' && invalidDistributionIdsSet.has(item.id),
              ) && (
                <Tooltip title={t('Completa los datos faltantes')} placement="right">
                  <ExclamationCircleOutlined className="ServiceOrderShippedItemsPackageWarning" />
                </Tooltip>
              )}
            </div>
            <div className="ServiceOrderShippedItemsPackageCount">
              {group.items.length > 1
                ? group.items.length + ' ' + t('Elementos')
                : group.items.length + ' ' + t('Elemento')}
            </div>
          </Flex>
        ),
        children: (
          <ServiceOrderShippedItemsTable
            packageGroup={group}
            setServiceOrderItems={setServiceOrderItems}
            serviceOrderItems={serviceOrderItems}
            invalidDistributionSet={invalidDistributionIdsSet}
          />
        ),
      })),

    [packageGroups, t, setServiceOrderItems, serviceOrderItems, invalidDistributionIdsSet],
  );

  return (
    <div className="ServiceOrderShippedItems">
      <div className="ServiceOrderShippedItemsSectionTitle">
        {t('Elementos enviados por Acronex')}
      </div>
      {!tickets || tickets.length === 0 ? (
        <Empty description={t('Selecciona tickets para ver los elementos enviados')} />
      ) : isLoading ? (
        <Skeleton active />
      ) : packagePanels.length === 0 ? (
        <Empty description={t('No hay elementos enviados asociados a los tickets seleccionados')} />
      ) : (
        <Collapse ghost items={packagePanels} />
      )}
      <Alert
        showIcon
        type="info"
        message={t('La cantidad utilizada no puede superar la cantidad enviada')}
      />
      {serviceOrderItems.length > 0 && hasInvalidDistributionFlag && (
        <Alert
          showIcon
          style={{ marginTop: 12 }}
          type={'error'}
          message={t('Se deben completar los datos faltantes de los elementos enviados')}
        />
      )}
    </div>
  );
};

export default ServiceOrderShippedItems;
